import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Icon, { IconType } from 'components/Icon';
import { DesignSystem } from 'dragonmouse-shared/interfaces';
import { selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import DsListItem from '../DsListItem';
import * as S from './styles';

interface LinkPanelProps {
	designSystems: DesignSystem[];
	onCreateNew: () => void;
	onLinkDs: (designSystem: DesignSystem | null) => Promise<void>;
}

const LinkPanel = ({ designSystems, onCreateNew, onLinkDs }: LinkPanelProps) => {
	const [searchQuery, setSearchQuery] = useState('');
	const isMobile = useSelector((r: RootReducer) => selectIsMobile(r.mode));

	// Sort design systems by updatedAt descending (same as sidebar)
	const sortedDesignSystems = useMemo(() => {
		return [...designSystems].sort(
			(a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
		);
	}, [designSystems]);

	// Filter design systems based on search query
	const filteredDesignSystems = useMemo(() => {
		if (!searchQuery.trim()) return sortedDesignSystems;
		return sortedDesignSystems.filter((ds) =>
			ds.title.toLowerCase().includes(searchQuery.toLowerCase())
		);
	}, [sortedDesignSystems, searchQuery]);

	// Calculate recent design systems (first 2, or all if < 5)
	const recentDesignSystems = useMemo(() => {
		if (sortedDesignSystems.length < 5) {
			return sortedDesignSystems;
		}
		return sortedDesignSystems.slice(0, 2);
	}, [sortedDesignSystems]);

	// Calculate all design systems (filtered, excluding recent ones)
	const allDesignSystems = useMemo(() => {
		return filteredDesignSystems.filter((ds) => !recentDesignSystems.some((r) => r.id === ds.id));
	}, [filteredDesignSystems, recentDesignSystems]);

	const handleClearSearch = () => {
		setSearchQuery('');
	};

	const hasSearchQuery = searchQuery.trim().length > 0;
	const showSearch = sortedDesignSystems.length >= 5;
	const showRecent = sortedDesignSystems.length > 0 && !hasSearchQuery;
	const showAllSection = sortedDesignSystems.length >= 5 && !hasSearchQuery;
	const showSearchResults = hasSearchQuery;
	const hasNoDesignSystems = sortedDesignSystems.length === 0;

	return (
		<>
			{!isMobile && (
				<S.DropdownSection>
					<S.CreateButton onClick={onCreateNew}>New Design System</S.CreateButton>
				</S.DropdownSection>
			)}

			{isMobile && hasNoDesignSystems && (
				<S.MobileEmptyState>
					No design systems yet. Create them on desktop to get started.
				</S.MobileEmptyState>
			)}

			{showSearch && (
				<>
					<S.SearchContainer>
						<S.SearchIcon>
							<Icon type={IconType.SEARCH} width={16} height={16} />
						</S.SearchIcon>
						<S.SearchInput
							type="text"
							placeholder="Search design systems..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							isMobile={isMobile}
						/>
						{searchQuery && (
							<S.ClearButton onClick={handleClearSearch} aria-label="Clear search">
								<Icon type={IconType.CLOSE} width={14} height={14} />
							</S.ClearButton>
						)}
					</S.SearchContainer>
					{!hasSearchQuery && (
						<S.SearchCount>
							{filteredDesignSystems.length} design system
							{filteredDesignSystems.length !== 1 ? 's' : ''}
						</S.SearchCount>
					)}
				</>
			)}

			{showSearchResults && (
				<>
					<S.SectionHeader>SEARCH RESULTS ({filteredDesignSystems.length})</S.SectionHeader>
					<S.ScrollArea>
						{filteredDesignSystems.length === 0 ? (
							<S.EmptyState>No design systems found</S.EmptyState>
						) : (
							filteredDesignSystems.map((ds) => (
								<DsListItem key={ds.id} item={ds} onClick={() => onLinkDs(ds)} />
							))
						)}
					</S.ScrollArea>
				</>
			)}

			{showRecent && (
				<>
					<S.SectionHeader>RECENT</S.SectionHeader>
					{recentDesignSystems.map((ds) => (
						<DsListItem key={ds.id} item={ds} onClick={() => onLinkDs(ds)} />
					))}
				</>
			)}

			{showAllSection && (
				<>
					<S.SectionHeader>ALL DESIGN SYSTEMS</S.SectionHeader>
					<S.ScrollArea>
						{allDesignSystems.length === 0 ? (
							<S.EmptyState>No design systems found</S.EmptyState>
						) : (
							allDesignSystems.map((ds) => (
								<DsListItem key={ds.id} item={ds} onClick={() => onLinkDs(ds)} />
							))
						)}
					</S.ScrollArea>
				</>
			)}
		</>
	);
};

export default LinkPanel;
